import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { OPTION_STATUS } from "@miplatform/constants";
import { IBidPricingResponse } from "@miplatform/types";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import {
  COLUMN_WIDTH,
  EMPTY_DRAW_ROW,
  IDrawRow,
  formatPrice,
  getColumns,
  getDrawValue,
  groupDrops,
} from "../../../utils/priceDetails";

export function OptionPriceDetails({ priceDetails }: OptionPriceDetailsProps) {
  const { classes, cx } = useStyles();

  const planAreas = priceDetails.planAreas ?? [];

  const columns = useMemo(() => getColumns(priceDetails), [priceDetails]);
  const dropsSummary = priceDetails.dropsSummary ?? EMPTY_DRAW_ROW;
  const itemsSummary = priceDetails.materialsSummary ?? EMPTY_DRAW_ROW;

  const renderDrawValue = (row: IDrawRow, isRemoved?: boolean) => (
    <Grid container wrap="nowrap" spacing={1} className={classes.valueRow}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        const isValueRemoved = isRemoved || (value ?? 0) < 0;

        return (
          <Grid key={column} className={classes.columnWidth}>
            <Typography
              className={cx(
                classes.valueCell,
                isValueRemoved ? classes.valueRemoved : classes.valueAdded
              )}
            >
              {formatPrice(value, isValueRemoved)}
            </Typography>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Typography
          className={cx(
            classes.valueCell,
            isRemoved || (row.total ?? 0) < 0
              ? classes.valueRemoved
              : classes.valueAdded
          )}
        >
          {formatPrice(row.total, isRemoved)}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderSection = (type: "drops" | "items") => {
    const isDrops = type === "drops";
    const summary = isDrops ? dropsSummary : itemsSummary;

    return (
      <Accordion defaultExpanded disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={classes.sectionHeader}
        >
          <Typography>{isDrops ? "Drops" : "Items"}</Typography>
          <Box className={classes.drawsWrapper}>
            <DrawsRow row={summary} columns={columns} />
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.sectionBody}>
          {planAreas.map((area) => {
            const isAreaRemoved = area.optionStatus === OPTION_STATUS.REMOVED;
            const isAreaAdded = area.optionStatus === OPTION_STATUS.ADDED;
            const areaDrops = isDrops
              ? area.materials.flatMap((material) => {
                  const isMaterialRemoved =
                    isAreaRemoved ||
                    material.optionStatus === OPTION_STATUS.REMOVED ||
                    (material.total ?? 0) < 0;

                  return (material.drops ?? []).map((drop) => ({
                    ...drop,
                    isRemoved: isMaterialRemoved || (drop.price ?? 0) < 0,
                  }));
                })
              : [];

            if (isDrops && !areaDrops.length) return null;

            return (
              <Accordion
                key={area.planAreaId}
                defaultExpanded
                disableGutters
                className={classes.areaAccordion}
              >
                <AccordionSummary
                  expandIcon={<MiIcon path={mdiChevronDown} />}
                  className={classes.areaHeader}
                >
                  <Typography
                    className={cx(
                      classes.mediumText,
                      isAreaRemoved && classes.areaRemoved,
                      isAreaAdded && classes.areaAdded
                    )}
                  >
                    {area.planAreaLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.areaBody}>
                  {isDrops
                    ? groupDrops(areaDrops, true).map((drop) => (
                        <Box
                          key={`${drop.dropTypeId}-${
                            drop.isRemoved ? "removed" : "added"
                          }`}
                          className={classes.leafRow}
                        >
                          <Typography
                            className={cx(
                              classes.highlightText,
                              drop.isRemoved
                                ? classes.itemRemoved
                                : classes.itemAdded
                            )}
                          >
                            {`${drop.count}x ${drop.dropTypeName}`}
                          </Typography>
                          {renderDrawValue(
                            { draws: drop.draws, total: drop.total },
                            drop.isRemoved
                          )}
                        </Box>
                      ))
                    : area.materials.map((material, index) => {
                        const isItemRemoved =
                          isAreaRemoved ||
                          material.optionStatus === OPTION_STATUS.REMOVED;

                        return (
                          <Box
                            key={`${material.planMaterialId}-${material.materialId}-${index}`}
                            className={classes.leafRow}
                          >
                            <Typography
                              className={cx(
                                classes.highlightText,
                                isItemRemoved
                                  ? classes.itemRemoved
                                  : classes.itemAdded
                              )}
                            >
                              {`${material.quantity}x ${[
                                material.manufacturerPartNumber,
                                material.itemDescription,
                              ]
                                .filter(Boolean)
                                .join(" | ")}`}
                            </Typography>
                            {renderDrawValue(material, isItemRemoved)}
                          </Box>
                        );
                      })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (!planAreas.length) return null;

  return (
    <>
      {renderSection("drops")}
      {renderSection("items")}
    </>
  );
}

function DrawsRow({ row, columns }: DrawsRowProps) {
  const { classes, cx } = useStyles();

  return (
    <Grid container wrap="nowrap" spacing={1}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        const isNegative = (value ?? 0) < 0;

        return (
          <Grid key={column} className={classes.columnWidth}>
            <Box className={classes.drawCell}>
              <Typography
                className={cx(classes.mediumText, classes.truncatedText)}
              >
                {column}
              </Typography>
              <Typography
                className={cx(
                  classes.mediumText,
                  isNegative ? classes.valueRemoved : classes.valueAdded
                )}
              >
                {formatPrice(value)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Box
          className={cx(
            classes.drawCell,
            (row.total ?? 0) < 0
              ? classes.totalDrawCellRemoved
              : classes.totalDrawCell
          )}
        >
          <Typography className={classes.mediumText}>Total</Typography>
          <Typography
            className={cx(
              classes.mediumText,
              (row.total ?? 0) < 0 ? classes.valueRemoved : classes.valueAdded
            )}
          >
            {formatPrice(row.total)}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

export function OptionPriceSummary({ priceDetails }: OptionPriceDetailsProps) {
  const columns = useMemo(() => getColumns(priceDetails), [priceDetails]);

  return <DrawsRow row={priceDetails} columns={columns} />;
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    width: "100%",
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
  },
  sectionHeader: {
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.grey.light,
    minHeight: 48,
    "&.Mui-expanded": { minHeight: 48 },
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      margin: 0,
      "&.Mui-expanded": { margin: 0 },
    },
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  drawsWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  drawCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    borderRadius: 4,
    backgroundColor: theme.palette.primary.light,
    overflow: "hidden",
    padding: theme.spacing(0, 0.5),
  },
  totalDrawCell: {
    backgroundColor: theme.palette.green.light,
  },
  totalDrawCellRemoved: {
    backgroundColor: theme.palette.red.light,
  },
  areaAccordion: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    padding: theme.spacing(1, 2),
    backgroundColor: "transparent",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
  },
  areaHeader: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 32,
    "&.Mui-expanded": { minHeight: 32 },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "&.Mui-expanded": { margin: 0 },
    },
  },
  areaBody: {
    padding: 0,
  },
  leafRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.grey.mediumLight}`,
  },
  valueRow: {
    flexShrink: 0,
    marginRight: 24,
  },
  valueCell: {
    boxSizing: "border-box",
    textAlign: "center",
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
  },
  valueAdded: {
    color: theme.palette.green.main,
  },
  valueRemoved: {
    color: theme.palette.red.main,
  },
  mediumText: {
    fontSize: theme.fontSize.medium,
  },
  highlightText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  itemAdded: {
    color: theme.palette.green.main,
  },
  itemRemoved: {
    color: theme.palette.red.main,
  },
  areaAdded: {
    color: theme.palette.green.main,
  },
  areaRemoved: {
    color: theme.palette.red.main,
    textDecoration: "line-through",
  },
  truncatedText: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  columnWidth: {
    width: COLUMN_WIDTH,
  },
}));

interface OptionPriceDetailsProps {
  priceDetails: IBidPricingResponse;
}

interface DrawsRowProps {
  row: IDrawRow;
  columns: string[];
}
